/**
 * step-template.tsx — Bước 1 của luồng tạo nhân viên AI: chọn mẫu (Bán hàng · Chăm sóc khách hàng · Đặt lịch hẹn · Bắt đầu
 * trống), đặt tên + ảnh emoji, xem / sửa nhân vật (tuỳ chọn: mẫu nhân vật theo ngành của máy chủ), kỹ năng gợi ý.
 * Bấm "Tiếp" → `POST /ai/bots` tạo bản nháp NGAY (tắt, chưa giao kênh) — từ đây chat thử được. Đã có bản nháp → `PUT`.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, PlugZap, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { usePersonaTemplates } from '../train/train-api'
import { PERSONA_MAX_CHARS } from '../train/bot-settings'
import {
  BOT_EMOJIS, BOT_NAME_MAX, WIZARD_TEMPLATES, defaultToolsJson, templateByKey, templateSettings,
  type WizardTemplate, type WizardTemplateKey,
} from './wizard-templates'
import { createTemplateSkills, createWizardBot, updateWizardBot, useBotSkills, type WizardBot } from './wizard-api'
import { scheduleFields } from './wizard-lib'
import { useAiModelsBySource, useAiSources } from '@/hooks/use-ai-credits'

/** Cảnh báo "chưa phụ trách kênh" là chủ đích ở bước này (giao kênh ở bước 4) — không hiện. */
const isNoChannelWarning = (w: string) => /chưa phụ trách kênh/i.test(w) // i18n-ignore: khớp thông báo tiếng Việt của máy chủ

export function StepTemplate({ bot, templateKey, presetModel, onSaved, onOpenExternal }: {
  bot: WizardBot | null
  templateKey: WizardTemplateKey | null
  /** Từ lối "Tạo bằng Key của tôi": bot MỚI tạo chạy model này (nguồn Key của tôi của nhà cung cấp đó). */
  presetModel?: { provider: string; model: string } | null
  /** Đã tạo / lưu bản nháp → sang bước 2. */
  onSaved: (bot: WizardBot, templateKey: WizardTemplateKey) => void
  /** Mở hộp "Thêm nhân viên AI" (Tạo bằng Key của tôi / Kết nối bot bên ngoài). */
  onOpenExternal: () => void
}) {
  const { t } = useTranslation('aiWizard')
  const [selected, setSelected] = useState<WizardTemplateKey | null>(templateKey)
  const tpl = templateByKey(selected)
  const [name, setName] = useState(bot?.name ?? '')
  const [emoji, setEmoji] = useState(bot?.avatarEmoji ?? '')
  const [persona, setPersona] = useState<string | null>(null) // null = theo mẫu
  const [showPersona, setShowPersona] = useState(false)
  const [withSkills, setWithSkills] = useState(true)
  const [saving, setSaving] = useState(false)
  const { data: skills } = useBotSkills(bot?.id ?? null)
  const personaTemplatesQ = usePersonaTemplates(!!tpl?.personaGroup)
  const industryTemplates = useMemo(
    () => (personaTemplatesQ.data?.templates ?? []).filter((x) => x.group === tpl?.personaGroup),
    [personaTemplatesQ.data, tpl?.personaGroup],
  )

  // Bản nháp nạp xong sau lần render đầu → điền tên / emoji của nó.
  useEffect(() => {
    if (!bot) return
    setName((n) => n || bot.name)
    setEmoji((e) => e || bot.avatarEmoji || '')
  }, [bot])

  function pick(tp: WizardTemplate) {
    setSelected(tp.key)
    setPersona(null)
    // Tên / emoji còn đang theo mẫu cũ (hoặc trống) → đổi theo mẫu mới.
    const prev = templateByKey(selected)
    if (!name.trim() || (prev && name === prev.defaultName)) setName(tp.defaultName)
    if (!emoji || (prev && emoji === prev.emoji)) setEmoji(tp.emoji)
  }

  const personaText = persona ?? tpl?.persona ?? ''
  const needsFill = personaText.includes('[cần điền') // i18n-ignore: dấu giữ chỗ trong nội dung mẫu (dữ liệu tiếng Việt)
  const templateChanged = !!bot && !!selected && selected !== templateKey
  const hasTemplateSkills = (skills ?? []).some((s) => !!selected && s.key.startsWith(`${selected}-`))
  const nameTrim = name.trim()
  const problem = !selected && !bot ? t('template.pickOne')
    : !nameTrim ? t('template.nameRequired')
      : nameTrim.length > BOT_NAME_MAX ? t('template.nameMax', { max: BOT_NAME_MAX })
        : personaText.length > PERSONA_MAX_CHARS ? t('template.personaMax', { max: PERSONA_MAX_CHARS.toLocaleString(currentLocale()) })
          : null

  async function submit() {
    if (problem) { toast.error(problem); return }
    if (!tpl) {
      // Bản nháp mở lại mà không rõ mẫu: chỉ lưu tên / ảnh.
      if (!bot) return
      setSaving(true)
      try {
        const res = await updateWizardBot(bot.id, { name: nameTrim, avatarEmoji: emoji || null })
        onSaved(res.bot, templateKey ?? 'trong')
      } catch (err) {
        toast.error(apiError(err))
      } finally {
        setSaving(false)
      }
      return
    }
    setSaving(true)
    try {
      const content = {
        personaPrompt: personaText.trim() || null,
        playbookPrompt: tpl.playbook || null,
        description: tpl.key === 'trong' ? null : tpl.summaryLuu,
        settings: templateSettings(tpl),
      }
      let saved: WizardBot
      let warnings: string[] = []
      if (!bot) {
        const res = await createWizardBot({
          kind: 'builtin', name: nameTrim, avatarEmoji: emoji || tpl.emoji, enabled: false, channelAccountIds: [],
          toolsJson: defaultToolsJson(), ...scheduleFields('q4'), ...content,
          ...(presetModel ? { provider: presetModel.provider, model: presetModel.model } : {}),
        })
        saved = res.bot
        warnings = res.warnings ?? []
      } else {
        const res = await updateWizardBot(bot.id, {
          name: nameTrim, avatarEmoji: emoji || null,
          // Đổi mẫu (hoặc sửa nhân vật) → áp nội dung mẫu mới; giữ nguyên thì chỉ đổi tên / ảnh.
          ...(templateChanged || persona !== null ? content : {}),
        })
        saved = res.bot
        warnings = res.warnings ?? []
      }
      if (withSkills && tpl.skills.length && !hasTemplateSkills) {
        const r = await createTemplateSkills(saved.id, tpl.key, tpl.skills)
        if (r.failed) toast.warning(t('template.skillsFailed', { failed: r.failed, total: tpl.skills.length }))
      }
      for (const w of warnings.filter((x) => !isNoChannelWarning(x))) toast.warning(w)
      toast.success(bot ? t('template.saved') : t('template.draftCreated', { name: saved.name }))
      onSaved(saved, tpl.key)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-medium">{t('template.choose')}</h2>
          <p className="text-sm text-muted-foreground">{t('template.chooseDesc')}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t('template.groupAria')}>
          {WIZARD_TEMPLATES.map((tp) => {
            const active = selected === tp.key
            return (
              <button
                key={tp.key} type="button" role="radio" aria-checked={active} onClick={() => pick(tp)}
                className={cn(
                  'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                  active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:border-primary/50 hover:bg-muted/40',
                )}
              >
                <span className="text-2xl leading-none" aria-hidden>{tp.emoji}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    {tp.title}
                    {active && <CheckCircle2 className="h-4 w-4 text-primary" />}
                    {bot && templateKey === tp.key && !active && <span className="text-[11px] font-normal text-muted-foreground">{t('template.inUse')}</span>}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{tp.summary}</span>
                  {tp.skills.length > 0 && (
                    <span className="mt-1.5 block text-[11px] text-muted-foreground">{t('template.skills')} {tp.skills.map((s) => s.label).join(' · ')}</span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
        {!bot && presetModel && <PresetModelNote preset={presetModel} />}
        <button type="button" onClick={onOpenExternal} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary hover:underline">
          <PlugZap className="h-3.5 w-3.5" /> {t('template.external')}
        </button>
      </section>

      {(tpl || bot) && (
        <section className="space-y-4 rounded-xl border p-4">
          {templateChanged && tpl && (
            <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
              {t('template.changeWarn', { title: tpl?.title })}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div className="space-y-1.5">
              <Label htmlFor="wz-name">{t('template.nameLabel')}</Label>
              <Input id="wz-name" value={name} maxLength={BOT_NAME_MAX + 10} onChange={(e) => setName(e.target.value)} placeholder={tpl?.defaultName ?? t('template.nameLabel')} />
              <p className="text-[11px] text-muted-foreground">{t('template.nameHint')}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t('template.image')}</Label>
              <div className="flex max-w-[16rem] flex-wrap gap-1" role="radiogroup" aria-label={t('template.emojiAria')}>
                {BOT_EMOJIS.map((e) => (
                  <button
                    key={e} type="button" role="radio" aria-checked={emoji === e} onClick={() => setEmoji(e)}
                    className={cn('h-8 w-8 rounded-md border text-lg leading-none', emoji === e ? 'border-primary bg-primary/10' : 'hover:bg-muted')}
                  >{e}</button>
                ))}
              </div>
            </div>
          </div>

          {tpl && <div className="space-y-2">
            <button type="button" className="inline-flex items-center gap-1 text-sm font-medium" onClick={() => setShowPersona((v) => !v)} aria-expanded={showPersona}>
              <ChevronDown className={cn('h-4 w-4 transition-transform', !showPersona && '-rotate-90')} /> {t('template.viewPersona')}
              {persona !== null && <span className="text-xs font-normal text-muted-foreground">{t('template.edited')}</span>}
            </button>
            {showPersona && (
              <div className="space-y-2">
                {industryTemplates.length > 0 && (
                  <div className="space-y-1">
                    <Label>{t('template.industryLabel')}</Label>
                    <Select value="" onValueChange={(id) => { const it = industryTemplates.find((x) => x.id === id); if (it) setPersona(it.content) }}>
                      <SelectTrigger className="h-9"><SelectValue placeholder={t('template.industryPlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {industryTemplates.map((it) => <SelectItem key={it.id} value={it.id}>{it.industry} — {it.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Textarea
                  className="min-h-56 font-mono text-xs" value={personaText} onChange={(e) => setPersona(e.target.value)}
                  placeholder={t('template.personaPlaceholder')} aria-label={t('template.personaAria')}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <span>
                    {needsFill ? t('template.fillWarn') : t('template.personaHint')}
                  </span>
                  <span>
                    {personaText.length.toLocaleString(currentLocale())} / {PERSONA_MAX_CHARS.toLocaleString(currentLocale())}
                    {persona !== null && <button type="button" className="ml-2 text-primary hover:underline" onClick={() => setPersona(null)}>{t('template.backToTemplate')}</button>}
                  </span>
                </div>
              </div>
            )}
          </div>}

          {tpl && tpl.skills.length > 0 && (
            hasTemplateSkills ? (
              <p className="text-xs text-muted-foreground">{t('template.skillsCreated', { count: tpl.skills.length })}</p>
            ) : (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={withSkills} onCheckedChange={(v) => setWithSkills(v === true)} className="mt-0.5" />
                <span>
                  {t('template.skillsCreate', { count: tpl.skills.length, names: tpl.skills.map((s) => s.label).join(', ') })}
                  <span className="block text-xs text-muted-foreground">{t('template.skillsHint')}</span>
                </span>
              </label>
            )
          )}
        </section>
      )}

      <div className="flex items-center justify-end gap-2">
        {problem && selected && <span className="text-xs text-muted-foreground">{problem}</span>}
        <Button onClick={() => void submit()} disabled={saving || !!problem}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {bot ? t('template.saveNext') : t('template.createNext')}
        </Button>
      </div>
    </div>
  )
}

/** Dòng nhắc model đặt sẵn (lối Key của tôi) — tên model theo danh mục "Key của tôi". */
function PresetModelNote({ preset }: { preset: { provider: string; model: string } }) {
  const { t } = useTranslation('aiWizard')
  const { data: sources } = useAiSources()
  const { data: models } = useAiModelsBySource('byok')
  const providerName = sources?.providers.find((p) => p.provider === preset.provider)?.name ?? preset.provider
  const title = models?.models.find((m) => m.provider === preset.provider && m.model === preset.model)?.title ?? preset.model
  return (
    <p className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
      <KeyRound className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span><Trans t={t} i18nKey="template.presetModel" values={{ provider: providerName, title }} components={{ b: <b /> }} /></span>
    </p>
  )
}
